/**
 * SteamDeal API client — proxies through Cloudflare Functions → steamdeal.co.kr
 */
// 토스 인앱(.ait)에서는 tossmini.com에서 호스팅되므로
// steamdeal.co.kr API 직접 호출 시 CORS 차단됨
// → Cloudflare Pages 프록시(steamdeal-toss.pages.dev/api)를 경유
export const API_BASE =
  typeof window !== "undefined" && window.location.hostname.includes("tossmini.com")
    ? "https://steamdeal-toss.pages.dev/api"
    : "/api";

async function fetchJSON<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
  return res.json();
}

/** URL builder (returns path string for manual fetch with signal) */
function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export const api = {
  // Charts
  charts: (lang = "ko") =>
    fetchJSON(`/store/charts?lang=${lang}`),

  // Top 100 games
  topGames: (lang = "ko") =>
    fetchJSON(`/store/top-games?lang=${lang}`),

  // Search games
  search: (query: string, start = 0, count = 100, lang = "ko") =>
    fetchJSON(`/store/search?query=${encodeURIComponent(query)}&start=${start}&count=${count}&lang=${lang}`),

  // Store browse (with filters)
  storeBrowse: (params: Record<string, string | number>) => {
    const qs = Object.entries(params).filter(([, v]) => v !== "" && v !== undefined).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    return fetchJSON(`/store/search?${qs}`);
  },

  // Deals
  deals: (start = 0, count = 100, sort = "discount", lang = "ko") =>
    fetchJSON(`/store/search?specials=1&start=${start}&count=${count}&sort_by=${sort}&lang=${lang}`),

  // App details
  appDetails: (appId: number | string, lang = "ko") =>
    fetchJSON(`/store/appdetails?appids=${appId}&lang=${lang}`),

  // Free games
  freeGames: (lang = "ko") =>
    fetchJSON(`/free-games?lang=${lang}`),

  // Game search (autocomplete)
  gameSearch: (query: string, lang = "ko") =>
    fetchJSON(`/store/game-search?term=${encodeURIComponent(query)}&lang=${lang}`),

  // Tag games
  tagGames: (tag: string, lang = "ko", start = 0) =>
    fetchJSON(`/store/tag-games?tag=${encodeURIComponent(tag)}&lang=${lang}&start=${start}`),

  // Upcoming / releases
  upcoming: (type: string, lang = "ko", start = 0, count = 50) =>
    fetchJSON(`/store/upcoming?type=${type}&lang=${lang}&start=${start}&count=${count}`),

  // Price history (ITAD)
  priceHistory: (appId: number | string, lang = "ko") =>
    fetchJSON(`/price-history?appid=${appId}&lang=${lang}`),

  // Featured categories (for calendar)
  featuredCategories: (lang = "ko") =>
    fetchJSON(`/store/featured-categories?lang=${lang}`),

  // Sales
  sales: (start = 0, count = 50, sort = "", lang = "ko") =>
    fetchJSON(`/store/sales?start=${start}&count=${count}&sort=${sort}&lang=${lang}`),

  // Batch prices
  batchPrices: (appids: string, lang = "ko") =>
    fetchJSON(`/store/batch-prices?appids=${appids}&lang=${lang}`),

  /* ── URL builders for manual fetch (supports AbortController) ── */
  store: {
    appDetails: (appId: string, lang = "ko") =>
      apiUrl(`/store/appdetails?appids=${appId}&lang=${lang}`),
    players: (appId: string) =>
      apiUrl(`/store/players?appid=${appId}`),
    achievements: (appId: string, lang = "ko") =>
      apiUrl(`/store/achievements?appid=${appId}&lang=${lang}`),
    tags: (appId: string, lang = "ko") =>
      apiUrl(`/store/tags?appid=${appId}&lang=${lang}`),
    reviews: (opts: { appid: string; filter?: string; num_per_page?: number; cursor?: string; language?: string; lang?: string }) => {
      const p = new URLSearchParams();
      p.set("appid", opts.appid);
      if (opts.filter) p.set("filter", opts.filter);
      if (opts.num_per_page !== undefined) p.set("num_per_page", String(opts.num_per_page));
      if (opts.cursor) p.set("cursor", opts.cursor);
      if (opts.language) p.set("language", opts.language);
      if (opts.lang) p.set("lang", opts.lang);
      return apiUrl(`/store/reviews?${p.toString()}`);
    },
    similar: (appId: string, lang = "ko") =>
      apiUrl(`/store/similar?appid=${appId}&lang=${lang}`),
    guides: (appId: string, lang = "ko", sort = "toprated") =>
      apiUrl(`/store/guides?appid=${appId}&lang=${lang}&sort=${sort}`),
    deckCompat: (appId: string, lang = "ko") =>
      apiUrl(`/store/deck-compat?appid=${appId}&lang=${lang}`),
    bundles: (appId: string, lang = "ko") =>
      apiUrl(`/store/bundles?appid=${appId}&lang=${lang}`),
    featuredCategories: (lang = "ko") =>
      apiUrl(`/store/featured-categories?lang=${lang}`),
    gameSearch: (query: string, lang = "ko") =>
      apiUrl(`/store/game-search?term=${encodeURIComponent(query)}&lang=${lang}`),
  },
  news: (opts: { appid: string; count?: number; maxlength?: number }) => {
    const p = new URLSearchParams();
    p.set("appid", opts.appid);
    if (opts.count !== undefined) p.set("count", String(opts.count));
    if (opts.maxlength !== undefined) p.set("maxlength", String(opts.maxlength));
    return apiUrl(`/news?${p.toString()}`);
  },
  freeGamesUrl: () => apiUrl("/free-games"),
};
